import { Start02Icon } from '@forwardflow/icons'
import { ContentCard, Group, Stack, Checkbox, TextInput } from '@forwardflow/ui-v2'
import cn from 'classnames'
import { useState } from 'react'
import { IFilterCriteria } from '../interfaces'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants'
import { useCampaignFilterContext } from '../hooks'
import { ActivityTriggersCard } from './ActivityTriggersCard'
import { FilterDefinition } from './FilterDefinition'

export const KindsOfLeads = ({ showFilterSummary = true }: { showFilterSummary?: boolean }) => {
   const {
      updateConfiguration,
      configuration,
      getFirstFilterCriteria,
      getRestFilterCriterias,
   } = useCampaignFilterContext()
   const firstFilterCriteria = getFirstFilterCriteria()
   const restFilterCriterias = getRestFilterCriterias()

   const existingBufferTime =
      configuration?.first_to_contact?.buffer_time || 0
   const [hasDelay, setHasDelay] = useState<boolean>(
      existingBufferTime > 0 ? true : false
   )
   const [delayMinutes, setDelayMinutes] = useState<string>(
      existingBufferTime > 0 ? existingBufferTime.toString() : ''
   )

   const selectedTypes: string[] = configuration?.campaign_type || []
   const hasNewLead = selectedTypes.includes('new-lead')
   const hasActivity = selectedTypes.includes('activity')
   const isActivityOnly = hasActivity && selectedTypes.length === 1
   const showDelayOption = hasNewLead

   const [lookbackDays, setLookbackDays] = useState<string>(
      configuration?.activity_campaign_filter_criteria?.lookback_days?.toString() || '0'
   )

   const handleToggleCampaignType = (typeId: string) => {
      let newTypes: string[]
      if (selectedTypes.includes(typeId)) {
         newTypes = selectedTypes.filter((t) => t !== typeId)
      } else {
         newTypes = [...selectedTypes, typeId]
      }

      const newConfig: any = {
         campaign_type: newTypes,
      }

      // Set activity_campaign_filter_criteria when activity is selected, preserving any
      // events/triggers the user already picked (only lookback is type-dependent).
      if (newTypes.includes('activity')) {
         const isOnlyActivity = newTypes.length === 1 && newTypes[0] === 'activity'
         const existing = configuration?.activity_campaign_filter_criteria
         newConfig.activity_campaign_filter_criteria = {
            events: existing?.events?.length ? existing.events : ['all'],
            lookback_days: isOnlyActivity ? (parseInt(lookbackDays) || 0) : null,
            triggers: existing?.triggers ?? [],
         }
      } else {
         newConfig.activity_campaign_filter_criteria = null
      }

      // Reset delay if new-lead is removed
      if (!newTypes.includes('new-lead')) {
         setHasDelay(false)
         setDelayMinutes('')
         newConfig.first_to_contact = { buffer_time: 0 }
      }

      updateConfiguration(newConfig)
   }

   const handleDelayChange = (value: boolean) => {
      setHasDelay(value)
      if (value === false) {
         setDelayMinutes('')
         updateConfiguration({
            first_to_contact: {
               buffer_time: 0,
            },
         })
      }
   }

   const handleDelayMinutesChange = (value: string) => {
      setDelayMinutes(value)
      const minutes = parseInt(value)
      if (!isNaN(minutes) && minutes > 0) {
         updateConfiguration({
            first_to_contact: {
               buffer_time: minutes,
            },
         })
      }
   }

   const handleLookbackChange = (value: string) => {
      setLookbackDays(value)
      const days = parseInt(value)
      const existing = configuration?.activity_campaign_filter_criteria
      updateConfiguration({
         activity_campaign_filter_criteria: {
            events: existing?.events?.length ? existing.events : ['all'],
            lookback_days: !isNaN(days) ? days : 0,
            triggers: existing?.triggers ?? [],
         },
      })
   }

   return (
      <Stack>
         <ContentCard className="space-y-4 !p-0">
            {showFilterSummary && firstFilterCriteria?.field && (
               <div className="p-6 border-b border-[#272E35] space-y-4">
                  <Group gap={10}>
                     <Start02Icon />
                     <p className="text-white font-semibold text-lg">
                        Which leads should Forward Flow work on?
                     </p>
                  </Group>
                  <div className="space-y-2.5">
                     <FilterDefinition
                        filterCriteria={firstFilterCriteria as IFilterCriteria}
                     />
                     {restFilterCriterias.map((filterCriteria) => (
                        <FilterDefinition
                           key={filterCriteria.field}
                           filterCriteria={filterCriteria as IFilterCriteria}
                        />
                     ))}
                  </div>
               </div>
            )}
            <Stack className={showFilterSummary && firstFilterCriteria?.field ? "p-6 pt-0" : "p-6"}>
               <p className="text-white font-semibold text-lg">
                  Which type of leads should Forward Flow work on?
               </p>
               <p className="text-white/50 text-sm">
                  Select one or more options.
               </p>
               <Stack gap={16}>
                  {CAMPAIGN_TYPE_OPTIONS.map((option) => {
                     const isSelected = selectedTypes.includes(option.id)
                     return (
                        <button
                           key={option.id}
                           className={cn(
                              'rounded-[8px] bg-white/5 p-4 text-left flex items-center gap-4 transition-all',
                              isSelected &&
                                 'bg-[#A684FF1A] border border-[#A684FF]/40',
                              !isSelected &&
                                 'border border-transparent hover:bg-white/[0.08]'
                           )}
                           onClick={() => handleToggleCampaignType(option.id)}
                        >
                           <Checkbox
                              readOnly
                              checked={isSelected}
                              className="pointer-events-none"
                           />
                           <Group
                              className="rounded-[8px] p-2"
                              justify="center"
                              align="center"
                              style={{ backgroundColor: `${option.color}1A` }}
                           >
                              {option.icon}
                           </Group>
                           <Stack gap={2}>
                              <p className="text-white font-semibold text-lg">
                                 {option.label}
                              </p>
                              <p className="text-white/70 text-xs">
                                 {option.description}
                              </p>
                           </Stack>
                        </button>
                     )
                  })}
               </Stack>
            </Stack>
         </ContentCard>

         {/* Activity events + pond triggers — show whenever activity is selected */}
         {hasActivity && <ActivityTriggersCard />}

         {/* Activity Lookback - Show only when activity is the only type */}
         {isActivityOnly && (
            <ContentCard className="space-y-4 !p-6">
               <Stack gap={16}>
                  <div>
                     <p className="text-white font-medium text-base">
                        How far back should we look for activity?
                     </p>
                     <p className="text-white/50 text-sm mt-1">
                        Enter the number of days to check for recent lead activity. Leave at 0 to only respond to new events going forward.
                     </p>
                  </div>
                  <TextInput
                     label="Lookback (days)"
                     placeholder="0"
                     type="number"
                     min="0"
                     value={lookbackDays}
                     onChange={(e) => handleLookbackChange(e.target.value)}
                  />
               </Stack>
            </ContentCard>
         )}

         {/* Delay Selection - Show only for new-lead */}
         {showDelayOption && (
            <ContentCard className="space-y-4 !p-6">
               <Stack gap={16}>
                  <p className="text-white font-medium text-base">
                     Would you like us to set a delay, so your agents have time
                     to call new leads before Forward Flow calls them?
                  </p>

                  <Group gap={16}>
                     <button
                        className={cn(
                           'rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2 hover:bg-white/[0.05] transition-colors',
                           hasDelay === true &&
                              'bg-[#A684FF1A] border-[#A684FF]/40'
                        )}
                        onClick={() => handleDelayChange(true)}
                     >
                        <Group gap={8}>
                           <Checkbox readOnly checked={hasDelay === true} />
                           <p className="text-white text-sm font-medium">Yes</p>
                        </Group>
                     </button>

                     <button
                        className={cn(
                           'rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2 hover:bg-white/[0.05] transition-colors',
                           hasDelay === false &&
                              'bg-[#A684FF1A] border-[#A684FF]/40'
                        )}
                        onClick={() => handleDelayChange(false)}
                     >
                        <Group gap={8}>
                           <Checkbox readOnly checked={hasDelay === false} />
                           <p className="text-white text-sm font-medium">No</p>
                        </Group>
                     </button>
                  </Group>

                  {hasDelay === true && (
                     <Stack gap={8}>
                        <TextInput
                           label="Delay time (minutes)"
                           description="When a new lead is generated, how long would you like FF to wait prior to engaging, giving your agents time to take action."
                           placeholder="Enter delay in minutes"
                           type="number"
                           min="1"
                           value={delayMinutes}
                           onChange={(e) =>
                              handleDelayMinutesChange(e.target.value)
                           }
                        />
                        {delayMinutes && parseInt(delayMinutes) > 20 && (
                           <p className="text-[#FFA500] text-sm font-medium">
                              Warning: This can negatively impact your ROI on leads.
                              We recommend only setting a delay if you are sure
                              your team is going to call the leads.
                           </p>
                        )}
                     </Stack>
                  )}
               </Stack>
            </ContentCard>
         )}
      </Stack>
   )
}
